/**
 * V53 — the bilty list's filters live in the URL.
 *
 * `parseListSearch` turns whatever is in the query string into a safe shape
 * (unknown values fall back to defaults), `rangeFor` turns month / custom
 * into the `from_date` / `to_date` the API takes, and `monthOptions` feeds
 * the dropdown.
 */

export const LIST_STATUSES = [
  "all",
  "draft",
  "generated",
  "in_transit",
  "delivered",
  "invoiced",
  "settled",
  "cancelled",
] as const;
export type ListStatus = (typeof LIST_STATUSES)[number];

export const PAGE_SIZE = 50;

export type ListSearch = {
  /** "YYYY-MM", "all" or "custom". Missing = current month. */
  month?: string;
  /** Custom range, ISO dates; only read when month === "custom". */
  from?: string;
  to?: string;
  status?: ListStatus;
  q?: string;
  page?: number;
};

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function parseListSearch(raw: Record<string, unknown>): ListSearch {
  const out: ListSearch = {};
  const month = raw.month;
  if (typeof month === "string" && (month === "all" || month === "custom" || MONTH_RE.test(month))) {
    out.month = month;
  }
  if (typeof raw.from === "string" && DATE_RE.test(raw.from)) out.from = raw.from;
  if (typeof raw.to === "string" && DATE_RE.test(raw.to)) out.to = raw.to;
  if (typeof raw.status === "string" && (LIST_STATUSES as readonly string[]).includes(raw.status)) {
    out.status = raw.status as ListStatus;
  }
  if (typeof raw.q === "string" && raw.q.trim()) out.q = raw.q.trim();
  const page = typeof raw.page === "string" ? Number(raw.page) : raw.page;
  if (typeof page === "number" && Number.isInteger(page) && page > 1) out.page = page;
  return out;
}

export function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

/** The last `count` months, newest first, starting with the current one. */
export function monthOptions(now: Date, count = 24): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = monthKey(d);
    out.push({ value: key, label: monthLabel(key) });
  }
  return out;
}

function lastDayOf(year: number, month1: number): string {
  const d = new Date(year, month1, 0); // day 0 of next month = last day of this one
  return `${year}-${String(month1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** `from_date` / `to_date` for the API. Empty object = all time. */
export function rangeFor(search: ListSearch, now: Date): { from_date?: string; to_date?: string } {
  const month = search.month ?? monthKey(now);
  if (month === "all") return {};
  if (month === "custom") {
    const out: { from_date?: string; to_date?: string } = {};
    if (search.from) out.from_date = search.from;
    if (search.to) out.to_date = search.to;
    return out;
  }
  const [y, m] = month.split("-").map(Number);
  return { from_date: `${month}-01`, to_date: lastDayOf(y, m) };
}

/** What the header should say for the picked range. */
export function rangeLabel(search: ListSearch, now: Date): string {
  const month = search.month ?? monthKey(now);
  if (month === "all") return "All time";
  if (month === "custom") {
    if (search.from && search.to) return `${search.from} to ${search.to}`;
    if (search.from) return `From ${search.from}`;
    if (search.to) return `Up to ${search.to}`;
    return "Custom range";
  }
  return monthLabel(month);
}
